import { useMemo } from "react";
import { numberRuns, runTime } from "@/lib/run-trend";
import { useSetBreadcrumb } from "@/components/layout/breadcrumb-context";

interface UseWorkspaceRunsOptions {
  walkthrough: any;
  scenarioRuns: any[] | undefined;
  walkthroughId: string | undefined;
  baseRunId: string | null;
}

export function useWorkspaceRuns({
  walkthrough,
  scenarioRuns,
  walkthroughId,
  baseRunId,
}: UseWorkspaceRunsOptions) {
  const runNumber = useMemo(() => {
    if (!scenarioRuns || !walkthroughId) return undefined;
    return numberRuns(scenarioRuns).get(walkthroughId);
  }, [scenarioRuns, walkthroughId]);

  const runsChronological = useMemo(() => {
    if (!scenarioRuns) return [];
    return [...scenarioRuns].sort((a, b) => runTime(a) - runTime(b));
  }, [scenarioRuns]);

  const defaultBaseRunId = useMemo(() => {
    if (!runsChronological.length || !walkthroughId) return null;
    const currentIndex = runsChronological.findIndex(
      (r) => r.id === walkthroughId,
    );
    if (currentIndex > 0) {
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (runsChronological[i].status === "completed")
          return runsChronological[i].id;
      }
      return runsChronological[currentIndex - 1].id;
    }
    const otherRun = runsChronological.find((r) => r.id !== walkthroughId);
    return otherRun?.id ?? null;
  }, [runsChronological, walkthroughId]);

  const activeBaseRunId = useMemo(() => {
    if (baseRunId && runsChronological.some((r) => r.id === baseRunId))
      return baseRunId;
    return defaultBaseRunId;
  }, [baseRunId, runsChronological, defaultBaseRunId]);

  useSetBreadcrumb(
    runNumber ? `Run #${runNumber}` : (walkthrough?.scenario?.name ?? null),
  );

  return {
    runNumber,
    runsChronological,
    defaultBaseRunId,
    activeBaseRunId,
  };
}
